<template>
	<div class="stamp-main-page">
		<main class="stamp-app">
			<header class="stamp-topbar">
				<div class="stamp-brand">
					<img :src="logoImg" alt="2026 서울동물행복페스타" />
					<span class="stamp-brand-sep" aria-hidden="true"></span>
					<span class="stamp-brand-title">스탬프 투어</span>
				</div>
				<button v-if="festivals.length > 0" type="button" class="stamp-week-btn" @click="weekDialog = true">동물행복위크</button>
				<button v-if="user" type="button" class="stamp-logout" :title="user.name + '님 로그아웃'" @click="logout">
					<v-icon size="18">mdi-logout</v-icon>
				</button>
			</header>

			<!-- 메인 행사 이미지 (사업지 registration 등록 시 우선, 없으면 기본 비주얼) -->
			<section class="stamp-hero">
				<img :src="banner ? mediaUrl(banner) : heroImg" alt="2026 서울동물행복페스타 메인 비주얼" />
			</section>

			<section class="stamp-body">
				<aside class="stamp-notice" aria-label="경품 수령 안내">
					<div class="stamp-notice-icon">!</div>
					<div>
						<strong>경품 수령</strong>
						<p>경품 수령 시 행사 참여용 팔찌가 필요합니다. <b>팔찌가 없으신 분들은 반드시 현장 접수처를 방문해주세요.</b></p>
					</div>
				</aside>

				<div class="stamp-section-title">
					<h1>나의 스탬프</h1>
					<span>참여 현황을 확인해보세요</span>
				</div>

				<!-- 로딩 -->
				<div v-if="!loaded" class="stamp-main-loading">
					<v-progress-circular indeterminate color="#087DC5" size="48" />
				</div>

				<template v-else>
					<div v-if="cards.length === 0" class="stamp-main-empty">오늘 참여 가능한 스탬프 투어가 없습니다.</div>

					<article v-for="tour in cards" :key="tour.type + tour.id" class="stamp-tour-card" :class="{ 'is-done': isDoneState(tour) }">
						<!-- 상단: 썸네일 + 제목/진행 현황 + 상태 -->
						<div class="stamp-card-head">
							<div class="stamp-thumb" @click="tour.mainimg && openImageSrc(mainImgSrc(tour.mainimg))">
								<img v-if="tour.mainimg" :src="mainImgSrc(tour.mainimg)" :alt="tour.title" />
								<v-icon v-else color="#C9B24A" size="28">mdi-paw</v-icon>
							</div>
							<div class="stamp-meta">
								<h2>{{ tour.title }}</h2>
								<div class="stamp-progress">
									<template v-if="tour.type === 'quiz'">{{ quizCount(tour) }}문항</template>
									<template v-else>{{ collectedCount(tour) }} / {{ tour.groups.length }}</template>
									<small>{{ progressText(tour) }}</small>
								</div>
							</div>
							<span class="stamp-state" :class="{ done: isDoneState(tour) }">{{ stateLabel(tour) }}</span>
						</div>

						<!-- 스탬프 (원 + 명칭 라벨, 미획득/획득 동일 위치) -->
						<div
							v-if="tour.type !== 'quiz' && tour.groups.length > 0"
							class="stamp-row"
							:class="{ labeled: tour.type !== 'stamp-auto' }"
							:aria-label="`${tour.title} 스탬프 ${tour.groups.length}개 중 ${collectedCount(tour)}개 획득`"
						>
							<div v-for="(g, gi) in tour.groups" :key="gi" class="stamp-item">
								<div class="stamp-slot" :class="{ filled: g.collected, 'just-stamped': isJustStamped(tour, g) }">
									<img v-if="g.collected" :src="stampImg" alt="획득 스탬프" />
								</div>
								<span v-if="tour.type !== 'stamp-auto'" class="stamp-name">{{ g.title }}</span>
							</div>
						</div>

						<!-- 현장 참여(자동 적립) -->
						<div v-if="tour.type === 'stamp-auto'" class="stamp-completion-note">
							<span class="stamp-check-dot">✓</span>
							<span>참여 완료! 동물보호에 함께해 주셔서 감사합니다</span>
						</div>

						<!-- 상태별 하단 영역 -->
						<template v-else>
							<!-- 참가 완료 -->
							<template v-if="isFinished(tour)">
								<div class="stamp-reward-finished">
									✓ 참가 완료 처리되었습니다.
									{{
										tour.receipt || (tour.join && tour.join.winnterProduct) ? '경품 수령이 확인되었습니다.' : '함께해 주셔서 감사합니다.'
									}}
								</div>
								<button v-if="tour.endLink && !surveyDone" type="button" class="stamp-sub-btn" @click="openSurveyForTour(tour)">
									만족도 조사 참여하기
								</button>
							</template>

							<!-- 미당첨 종료 (미당첨자도 만족도 조사 참여 가능) -->
							<template v-else-if="tour.join && !tour.join.winnterProduct">
								<div class="stamp-reward-finished">아쉽지만 다음 기회에! · 참가 완료</div>
								<button v-if="tour.endLink && !surveyDone" type="button" class="stamp-sub-btn" @click="openSurveyForTour(tour)">
									만족도 조사 참여하기
								</button>
							</template>

							<!-- 당첨 → 경품 수령 대기 -->
							<div v-else-if="tour.join && tour.join.winnterProduct" class="stamp-reward-ready">
								<div class="stamp-reward-copy">
									<span class="stamp-gift-dot">★</span>
									<span
										><b>{{ tour.join.winnterProduct }}</b> 당첨! 경품 교환소에서 운영요원 확인 후 수령을 완료해주세요.</span
									>
								</div>
								<button type="button" class="stamp-reward-btn" @click="openPin(tour)">경품 수령 확인</button>
							</div>

							<!-- 완주 → 추첨/완료 진행 (룰렛/경품 이벤트 연결된 투어) -->
							<div v-else-if="tour.complete && tour.event" class="stamp-reward-ready">
								<div class="stamp-reward-copy">
									<span class="stamp-gift-dot">★</span>
									<span>{{ tour.type === 'quiz' ? '미션 성공!' : '스탬프를 모두 모았습니다.' }} 아래 버튼을 눌러 참여를 완료해주세요.</span>
								</div>
								<button type="button" class="stamp-reward-btn" :disabled="drawing" @click="startDraw(tour)">
									{{ drawing ? '진행 중...' : tour.event.type === 'roulette' ? '추첨 진행하기' : '참가 완료하기' }}
								</button>
							</div>

							<!-- 완주 → 이벤트 없는 투어 + 운영요원 확인 코드 설정(동물보호 PLAY존 등): 경품 수령 확인 → 참가 완료 + 만족도 조사 -->
							<div v-else-if="tour.complete && tour.receipt" class="stamp-reward-ready">
								<div class="stamp-reward-copy">
									<span class="stamp-gift-dot">★</span>
									<span
										>{{ tour.type === 'quiz' ? '미션 성공!' : '스탬프를 모두 모았습니다.' }} 스탬프투어 부스에서 진행요원 확인 후 수령을
										완료해주세요.</span
									>
								</div>
								<button type="button" class="stamp-reward-btn" @click="openPin(tour)">경품 수령 확인</button>
							</div>

							<!-- 완주 → 이벤트 없는 투어(동물행복런 등): 바로 참가 완료 + 만족도 조사 -->
							<div v-else-if="tour.complete" class="stamp-reward-ready">
								<div class="stamp-reward-copy">
									<span class="stamp-gift-dot">★</span>
									<span>{{ tour.type === 'quiz' ? '미션 성공!' : '스탬프를 모두 모았어요!' }} 참가 완료를 눌러주세요.</span>
								</div>
								<button type="button" class="stamp-reward-btn" @click="completeNoEvent(tour)">참가 완료하기</button>
							</div>

							<!-- OX 퀴즈 미션 -->
							<div v-else-if="tour.type === 'quiz'" class="stamp-reward-ready">
								<div class="stamp-reward-copy">
									<span class="stamp-gift-dot">?</span>
									<span>현장 키오스크에서 OX 퀴즈에 도전하고, 미션 성공 후 현장 담당자의 QR을 스캔하면 스탬프가 적립됩니다.</span>
								</div>
								<button type="button" class="stamp-reward-btn" @click="openScan">QR 스캔하기</button>
							</div>
						</template>
					</article>

					<p class="stamp-helper">QR 스캔 시 해당 구간 스탬프가 즉시 반영됩니다.</p>
				</template>
			</section>

			<!-- 하단 footer 네비게이션 -->
			<nav class="stamp-bottom-nav" aria-label="하단 메뉴">
				<button type="button" class="stamp-nav-item" @click="openScan">
					<span class="stamp-nav-icon"
						><svg viewBox="0 0 24 24"><path d="M9 3h6v4H9zM4 9h6v6H4zM14 9h6v6h-6zM9 17h6v4H9z" /></svg
					></span>
					<span>QR스캔</span>
				</button>
				<button type="button" class="stamp-nav-item" :class="{ active: activeTab === 'main' }" @click="goMain">
					<span class="stamp-nav-icon"
						><svg viewBox="0 0 24 24">
							<rect x="3" y="4" width="18" height="16" rx="2" />
							<path d="M7 9h5M7 13h10M7 17h7" /></svg
					></span>
					<span>내 스탬프</span>
				</button>
				<button type="button" class="stamp-nav-item" :class="{ active: activeTab === 'notice' }" @click="openNotice">
					<span class="stamp-nav-icon"
						><svg viewBox="0 0 24 24">
							<path d="M12 18h.01" />
							<path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 2-3 4" />
							<circle cx="12" cy="12" r="10" /></svg
					></span>
					<span>안내</span>
				</button>
			</nav>

			<!-- 경품 수령 확인 (운영요원 확인 코드 입력) 화면 -->
			<section v-if="pinDialog" class="stamp-claim-screen" aria-label="경품 수령 확인 코드 입력 화면">
				<header class="stamp-claim-topbar">
					<button type="button" class="stamp-claim-back" aria-label="뒤로가기" @click="pinDialog = false">‹</button>
					<strong>경품 수령 확인</strong>
				</header>
				<div class="stamp-claim-body">
					<div class="stamp-claim-logo"><img :src="logoImg" alt="2026 서울동물행복페스타" /></div>
					<div class="stamp-claim-card">
						<div class="stamp-claim-icon">✓</div>
						<h1>운영요원 확인 코드 입력</h1>
						<p class="lead">경품 수령을 위해 행사 운영요원이<br />{{ pinLength }}자리 확인 코드를 입력해주세요.</p>
						<label class="stamp-pin-label">확인 코드</label>
						<div class="stamp-pin-row" :style="{ gridTemplateColumns: `repeat(${pinLength}, minmax(0, 1fr))` }">
							<input
								v-for="(d, di) in pinDigits"
								:key="di"
								:ref="'pin' + di"
								class="stamp-pin-box"
								type="tel"
								inputmode="numeric"
								maxlength="1"
								autocomplete="off"
								:value="d"
								:aria-label="`확인 코드 ${di + 1}번째 자리`"
								@input="onPinInput(di, $event)"
								@keydown="onPinKeydown(di, $event)"
								@paste.prevent="onPinPaste"
							/>
						</div>
						<div class="stamp-staff-note">
							<span class="stamp-info-dot">i</span>
							<span>참가자가 직접 입력하지 않도록 운영요원이 경품 수령 여부를 확인한 뒤 코드를 입력해주세요.</span>
						</div>
						<div v-if="pinError" class="stamp-pin-error">{{ pinError }}</div>
						<button
							type="button"
							class="stamp-confirm-btn"
							:class="{ ready: pinReady }"
							:disabled="!pinReady || pinLoading"
							@click="submitPin"
						>
							{{ pinLoading ? '처리 중...' : '수령 완료 처리' }}
						</button>
					</div>
					<div class="stamp-claim-security">입력된 코드는 경품 수령 확인 용도로만 사용됩니다.</div>
				</div>
			</section>
		</main>

		<!-- 동물행복위크 (서울시 반려동물 행사 안내) 팝업 - 관리자 등록: 제목/날짜/링크 -->
		<v-dialog v-model="weekDialog" max-width="390" content-class="stamp-week-dialog">
			<section class="stamp-week-modal">
				<div class="stamp-week-head">
					<button type="button" class="stamp-week-close" aria-label="팝업 닫기" @click="weekDialog = false">×</button>
					<img class="stamp-week-logo" :src="logoImg" alt="2026 서울동물행복페스타" />
					<div class="stamp-week-kicker">2026 서울시 동물행복위크</div>
					<h2>서울시 반려동물 행사 안내</h2>
					<p>3주간 이어지는 반려동물과의 행복 동행.<br />다른 행사도 함께 확인해보세요.</p>
				</div>
				<div class="stamp-event-list">
					<component
						:is="f.link ? 'a' : 'div'"
						v-for="(f, fi) in festivals"
						:key="f.id || fi"
						class="stamp-event-card"
						:class="['no' + (fi % 3), { current: fi === 0 }]"
						:href="f.link || undefined"
						:target="f.link ? '_blank' : undefined"
						rel="noopener noreferrer"
					>
						<span class="stamp-event-no">{{ String(fi + 1).padStart(2, '0') }}</span>
						<span class="stamp-event-info">
							<strong>{{ f.title }}</strong>
							<span v-if="f.dateText">{{ f.dateText }}</span>
						</span>
						<span class="stamp-event-arrow">{{ f.link ? '›' : '' }}</span>
					</component>
				</div>
				<div class="stamp-week-benefit">
					<strong>동물행복위크 특별 이벤트</strong><br />3개 행사에 모두 참여하면 서울 댕냥데이즈 본부석에서 선물을 받을 수 있습니다.
				</div>
				<div class="stamp-week-bottom">
					<button type="button" @click="weekDialog = false">닫고 스탬프 확인하기</button>
				</div>
			</section>
		</v-dialog>

		<!-- QR 스캔 다이얼로그 -->
		<v-dialog v-model="scanDialog" fullscreen>
			<div class="stamp-scan-page" v-if="scanDialog">
				<div class="stamp-scan-head">
					<span>스탬프 QR 스캔</span>
					<v-btn icon dark @click="scanDialog = false"><v-icon>mdi-close</v-icon></v-btn>
				</div>
				<qrcode-stream :key="scanKey" :camera="cameraFacing" @decode="onDecode" @init="onCameraInit" class="stamp-scan-reader">
					<!-- QR 정렬 가이드 (이 안에 QR을 맞추면 잘 스캔됨) -->
					<div class="stamp-scan-frame">
						<span class="stamp-scan-corner tl"></span>
						<span class="stamp-scan-corner tr"></span>
						<span class="stamp-scan-corner bl"></span>
						<span class="stamp-scan-corner br"></span>
					</div>
				</qrcode-stream>
				<!-- 하단 footer: 안내 문구 + 카메라 전환 (검은 배경 / 흰 텍스트·아이콘, 한 줄) -->
				<div class="stamp-scan-footer">
					<span class="stamp-scan-footer-guide">스탬프 QR코드를 화면에 맞춰주세요</span>
					<button type="button" class="stamp-scan-footer-btn" @click="toggleCamera">
						<v-icon color="#fff" size="18">mdi-camera-flip</v-icon>
						<span>{{ cameraFacing === 'front' ? '전면' : '후면' }}</span>
					</button>
				</div>
			</div>
		</v-dialog>

		<!-- 안내(공지사항) 다이얼로그 -->
		<v-dialog v-model="noticeDialog" fullscreen>
			<div class="stamp-notice-page" v-if="noticeDialog">
				<div class="stamp-notice-head">
					<span>안내 · 공지사항</span>
					<v-btn icon @click="closeNotice"><v-icon>mdi-close</v-icon></v-btn>
				</div>
				<div class="stamp-notice-body">
					<div v-if="noticeLoading" class="stamp-notice-loading">
						<v-progress-circular indeterminate color="#087DC5" size="40" />
					</div>
					<div v-else-if="notices.length === 0" class="stamp-notice-empty">등록된 공지사항이 없습니다.</div>
					<template v-else>
						<div v-for="(n, ni) in notices" :key="n.id || ni" class="stamp-notice-card">
							<div class="stamp-notice-title">{{ n.title }}</div>
							<div class="stamp-notice-date" v-if="n.createdAt || n.date">{{ formatNoticeDate(n.createdAt || n.date) }}</div>
							<div class="stamp-notice-content">{{ n.content }}</div>

							<!-- 이미지 첨부 -->
							<div v-if="(n.images || []).length" class="stamp-notice-images">
								<img
									v-for="img in n.images"
									:key="'img' + img.id"
									:src="mediaUrl(img.url)"
									class="stamp-notice-img"
									@click="openImage(img)"
								/>
							</div>

							<!-- 파일 첨부 (다운로드) -->
							<div v-if="(n.files || []).length" class="stamp-notice-files">
								<a
									v-for="f in n.files"
									:key="'file' + f.id"
									:href="mediaUrl(f.url)"
									:download="f.name"
									target="_blank"
									rel="noopener"
									class="stamp-notice-file"
								>
									<v-icon size="18" color="#087DC5">mdi-file-download-outline</v-icon>
									<span class="stamp-notice-file-name">{{ f.name }}</span>
									<span class="stamp-notice-file-size" v-if="f.size">{{ formatFileSize(f.size) }}</span>
								</a>
							</div>
						</div>
					</template>
				</div>
			</div>
		</v-dialog>

		<!-- 공지 이미지 크게 보기 -->
		<v-dialog v-model="imageViewer.show" max-width="96vw">
			<div class="stamp-image-viewer" @click="imageViewer.show = false">
				<img :src="imageViewer.src" class="stamp-image-viewer-img" />
			</div>
		</v-dialog>

		<!-- 룰렛 다이얼로그 -->
		<v-dialog v-model="rouletteDialog" persistent max-width="380">
			<div class="stamp-roulette-box">
				<div class="stamp-roulette-title">리워드 룰렛</div>
				<div class="stamp-roulette-wrap">
					<div class="stamp-roulette-pin">▼</div>
					<div class="stamp-roulette-wheel" ref="wheel" :style="wheelStyle">
						<div v-for="(seg, si) in wheelSegments" :key="si" class="stamp-roulette-label" :style="segmentLabelStyle(si)">
							{{ seg }}
						</div>
					</div>
				</div>
				<template v-if="spinDone">
					<div class="stamp-roulette-result">
						{{ drawProduct ? `🎉 ${drawProduct} 당첨!` : '아쉽지만 다음 기회에!' }}
					</div>
					<button type="button" class="stamp-reward-btn" @click="closeRoulette">확인</button>
				</template>
			</div>
		</v-dialog>

		<!-- 만족도 조사 (바텀시트) -->
		<v-bottom-sheet v-model="surveyDialog" max-width="430">
			<section class="stamp-survey-sheet">
				<div class="stamp-sheet-handle"></div>
				<div class="stamp-survey-icon">♡</div>
				<h2>만족도 조사</h2>
				<p>
					{{ surveyTourTitle ? surveyTourTitle + ' 참여가 완료되었습니다.' : '참여해 주셔서 감사합니다.' }}<br />더 나은 행사 운영을 위해
					간단한 만족도 조사에 참여해주세요.
				</p>
				<button v-if="surveyLink" type="button" class="stamp-survey-primary" @click="openSurvey">만족도 조사 참여하기</button>
				<button type="button" class="stamp-survey-close" @click="surveyDialog = false">닫기</button>
			</section>
		</v-bottom-sheet>

		<v-snackbar v-model="toast.show" :color="toast.color" timeout="2500" top>{{ toast.text }}</v-snackbar>
	</div>
</template>

<script>
import axios from 'axios'
import heroImg from '@/assets/images/stamp/animal_festa_hero.jpg'
import logoImg from '@/assets/images/stamp/animal_festa_logo.png'
import stampImg from '@/assets/images/stamp/animal_festa_stamp.png'
import { loadStampUser, clearStampUser } from './stampUser'
const BACKEND = process.env.VUE_APP_BACKEND_URL
// 경품 수령 운영요원 확인 코드 자릿수 (관리자 투어 설정의 staffCode 와 동일해야 함)
const PIN_LENGTH = 4
// 한국시간 기준 오늘 날짜(YYYY-MM-DD) - 서버도 한국시간 자정 기준으로 스탬프를 새로 적립
const kstToday = () => new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10)

export default {
	metaInfo() {
		return { title: '모바일 스탬프 투어' }
	},
	data() {
		return {
			user: null,
			tours: [],
			banner: null,
			loaded: false,
			scanDialog: false,
			scanKey: 0,
			cameraFacing: 'rear',
			scanBusy: false,
			justStamped: { tourCode: '', groupTitle: '' }, // 방금 찍힌 스탬프(도장 연출용)
			drawing: false,
			rouletteDialog: false,
			rouletteTour: null,
			wheelRotate: 0,
			spinDone: false,
			drawProduct: '',
			pinDialog: false,
			pinTour: null,
			pinLength: PIN_LENGTH,
			pinDigits: Array(PIN_LENGTH).fill(''),
			pinError: '',
			pinLoading: false,
			surveyDialog: false,
			surveyLink: '',
			surveyTourTitle: '',
			surveyDone: false, // 만족도 조사 참여 여부 (참여자당 1회) - 반응형
			doneLocal: {}, // 이벤트 없는 투어(동물행복런 등) 로컬 참가완료 처리 (당일 한정)
			doneDate: '', // doneLocal 기준 날짜(한국시간 YYYY-MM-DD)
			activeTab: 'main',
			noticeDialog: false,
			notices: [],
			noticeLoading: false,
			imageViewer: { show: false, src: '' },
			festivals: [], // 동물행복위크(서울시 반려동물 행사 안내) - 관리자 등록
			weekDialog: false,
			heroImg,
			logoImg,
			stampImg,
			toast: { show: false, text: '', color: 'success' },
		}
	},
	computed: {
		groupcode() {
			return this.$route.params.groupcode
		},
		autoTours() {
			return this.tours.filter(t => t.type === 'stamp-auto')
		},
		stampTours() {
			return this.tours.filter(t => t.type === 'stamp')
		},
		// 현장 참여 → 스탬프 투어 순으로 한 목록(나의 스탬프)에 노출
		// OX 퀴즈는 동물보호 PLAY존의 멍쭈 존 미션이라 별도 카드로 노출하지 않는다 (퀴즈 화면 /stamp/:groupcode/quiz/:code 는 그대로 사용)
		cards() {
			return [...this.autoTours, ...this.stampTours]
		},
		pinCode() {
			return this.pinDigits.join('')
		},
		pinReady() {
			return this.pinDigits.every(d => /^\d$/.test(d))
		},
		wheelSegments() {
			if (!this.rouletteTour || !this.rouletteTour.event) return []
			return this.buildSegments(this.rouletteTour)
		},
		wheelStyle() {
			const n = this.wheelSegments.length || 1
			const arc = 360 / n
			const colors = ['#fde9a8', '#fff']
			const stops = this.wheelSegments.map((s, i) => `${colors[i % 2]} ${i * arc}deg ${(i + 1) * arc}deg`).join(', ')
			return {
				background: `conic-gradient(${stops})`,
				transform: `rotate(${this.wheelRotate}deg)`,
				transition: this.wheelRotate ? 'transform 4000ms cubic-bezier(0, 0, 0, 0.98)' : 'none',
			}
		},
	},
	created() {
		const saved = loadStampUser(this.groupcode)
		if (!saved) {
			this.$router.replace({ name: 'stampLogin', params: { groupcode: this.groupcode } })
			return
		}
		this.user = saved
		// 만족도 조사 참여 여부 복원 (참여자당 1회)
		this.surveyDone = sessionStorage.getItem(this.surveyKey()) === '1'
		// 이벤트 없는 투어의 로컬 참가완료 복원 (스탬프는 한국시간 자정마다 새로 찍으므로 오늘 날짜 기록만 사용)
		this.doneDate = kstToday()
		const done = sessionStorage.getItem('stamp-done-' + this.groupcode)
		if (done) {
			try {
				const parsed = JSON.parse(done)
				if (parsed && parsed.date === this.doneDate && Array.isArray(parsed.codes)) {
					parsed.codes.forEach(c => this.$set(this.doneLocal, c, true))
				}
			} catch (e) {
				console.log(e)
			}
		}
		this.fetchTours()
		this.fetchFestivals()
		// 화면을 켜 둔 채 자정이 지나도 다시 볼 때 오늘 기준 현황으로 갱신
		document.addEventListener('visibilitychange', this.onVisible)
	},
	beforeDestroy() {
		document.removeEventListener('visibilitychange', this.onVisible)
	},
	methods: {
		// 다시 화면에 돌아오면 적립 현황 재조회, 날짜(한국시간)가 바뀌었으면 전날 로컬 참가완료 초기화
		onVisible() {
			if (document.visibilityState !== 'visible' || !this.user) return
			const today = kstToday()
			if (today !== this.doneDate) {
				this.doneDate = today
				this.doneLocal = {}
				sessionStorage.removeItem('stamp-done-' + this.groupcode)
			}
			this.fetchTours()
		},
		// 투어 목록 + 적립 현황 조회 (현장 참여 스탬프는 서버에서 자동 적립)
		async fetchTours() {
			try {
				const res = await axios.get(BACKEND + '/stamp-tours', {
					params: { groupcode: this.groupcode, phone: this.user.phone, name: this.user.name },
				})
				if (res.data.result === 'ok') {
					this.tours = res.data.tours
					this.banner = res.data.banner || null
				}
			} catch (err) {
				console.log({ err })
				this.showToast('스탬프 정보를 불러오지 못했습니다.', 'error')
			} finally {
				this.loaded = true
			}
		},
		// 동물행복위크(행사 안내) 조회 - 로그인 직후 1회 자동 노출, 이후 상단 버튼으로 열기
		async fetchFestivals() {
			try {
				const params = { groupcode: this.groupcode }
				const business = sessionStorage.getItem('stamp-business-' + this.groupcode)
				if (business) params.business = business
				const res = await axios.get(BACKEND + '/stamp-festivals', { params })
				if (res.data.result === 'ok') this.festivals = res.data.festivals || []
			} catch (err) {
				console.log({ err })
			}
			const popupKey = 'stamp-week-popup-' + this.groupcode
			if (sessionStorage.getItem(popupKey) === '1') {
				sessionStorage.removeItem(popupKey)
				if (this.festivals.length > 0) this.weekDialog = true
			}
		},
		// 투어 메인 이미지 전체 경로 (상대경로면 백엔드 주소를 붙임)
		mainImgSrc(url) {
			if (!url) return ''
			return /^https?:\/\//.test(url) ? url : BACKEND + url
		},
		// 공지 첨부(이미지/파일) 전체 경로
		mediaUrl(url) {
			if (!url) return ''
			return /^https?:\/\//.test(url) ? url : BACKEND + url
		},
		// 파일 크기 표기
		formatFileSize(kb) {
			if (!kb && kb !== 0) return ''
			// strapi size 는 KB 단위(소수)
			const bytes = kb * 1024
			if (bytes < 1024) return Math.round(bytes) + 'B'
			if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + 'KB'
			return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
		},
		// 이미지 크게 보기
		openImage(img) {
			this.imageViewer = { show: true, src: this.mediaUrl(img.url) }
		},
		// 이미지 크게 보기 (전체 URL 직접 전달 - 투어 썸네일 등)
		openImageSrc(src) {
			if (!src) return
			this.imageViewer = { show: true, src }
		},
		isFinished(tour) {
			return tour.finished || !!this.doneLocal[tour.code]
		},
		// 이벤트(룰렛/경품) 없는 투어(예: 동물행복런): 완주 시 바로 참가완료 + 만족도 조사
		// 참가완료를 서버에 기록해 재로그인/세션 종료 후에도 완료 상태가 유지되도록 함
		async completeNoEvent(tour) {
			// 로컬에도 즉시 반영 (서버 재조회 전 UI 갱신)
			this.$set(this.doneLocal, tour.code, true)
			sessionStorage.setItem('stamp-done-' + this.groupcode, JSON.stringify({ date: this.doneDate, codes: Object.keys(this.doneLocal) }))
			let surveyLink = tour.endLink
			try {
				const res = await axios.post(BACKEND + '/stamp-complete', {
					groupcode: this.groupcode,
					phone: this.user.phone,
					name: this.user.name,
					code: tour.code,
				})
				if (res.data && res.data.surveyLink) surveyLink = res.data.surveyLink
			} catch (err) {
				console.log({ err })
			}
			// 만족도 조사는 참여자당 1회만 노출 (이미 참여했으면 스킵)
			if (surveyLink && !this.isSurveyDone()) {
				this.surveyLink = surveyLink
				this.surveyTourTitle = tour.title
				this.surveyDialog = true
				this.markSurveyDone()
			} else {
				this.showToast('참가 완료되었습니다!')
			}
		},
		collectedCount(tour) {
			return tour.groups.filter(g => g.collected).length
		},
		quizCount(tour) {
			return tour.quiz && tour.quiz.questions ? tour.quiz.questions.length : 0
		},
		// 카드 우측 상태 뱃지
		stateLabel(tour) {
			if (tour.type === 'stamp-auto') return '참여 완료'
			if (this.isFinished(tour)) return '참가 완료'
			if (tour.join) return tour.join.winnterProduct ? '수령 대기' : '참가 완료'
			if (tour.complete) return '미션 완료'
			if (tour.type !== 'quiz' && this.collectedCount(tour) > 0) return '참여 중'
			return '참여 전'
		},
		// 완료 계열 상태는 파란 계열(카드 좌측 바/뱃지)로 표시
		isDoneState(tour) {
			return tour.type === 'stamp-auto' || this.isFinished(tour) || !!tour.join || tour.complete
		},
		// 진행 현황 옆 보조 문구 (투어 설명은 관리자 등록 notice)
		progressText(tour) {
			if (tour.type === 'stamp-auto') return '번호 인증으로 자동 완료'
			if (this.isFinished(tour)) return tour.receipt || (tour.join && tour.join.winnterProduct) ? '경품 수령 완료' : '참가 완료'
			if (tour.complete) return '모든 미션을 완료했어요'
			if (tour.notice) return tour.notice
			return tour.type === 'quiz' ? 'OX 퀴즈 미션' : 'QR 스탬프를 스캔하면 스탬프가 적립됩니다'
		},
		// 방금 찍힌 스탬프인지 (도장 연출 대상)
		isJustStamped(tour, g) {
			return g.collected && tour.code === this.justStamped.tourCode && g.title === this.justStamped.groupTitle
		},
		// OX 퀴즈 참여 완료: 퀴즈 성공 후 적립된 스탬프(퀴즈 투어의 지점)가 모두 적립된 상태
		showToast(text, color) {
			this.toast = { show: true, text, color: color || 'success' }
		},
		// 로그아웃 → 저장된 참여자 정보(localStorage) 삭제 후 다른 이름/연락처로 재로그인 가능
		logout() {
			clearStampUser(this.groupcode)
			// 번호에 묶이지 않은 기기 저장값도 비워 다음 참여자에게 넘어가지 않게 한다
			sessionStorage.removeItem('stamp-done-' + this.groupcode)
			sessionStorage.removeItem('stamp-week-popup-' + this.groupcode)
			this.doneLocal = {}
			this.$router.replace({ name: 'stampLogin', params: { groupcode: this.groupcode } })
		},
		// QR 스캔
		openScan() {
			this.cameraFacing = 'rear' // 기본 후면 카메라
			this.scanKey++
			this.scanDialog = true
		},
		// 전/후면 카메라 전환
		toggleCamera() {
			this.cameraFacing = this.cameraFacing === 'rear' ? 'front' : 'rear'
		},
		// 카메라 초기화 (후면이 없으면 전면으로 폴백 - 데스크톱 등)
		async onCameraInit(promise) {
			try {
				await promise
			} catch (err) {
				console.log({ err })
				if (this.cameraFacing === 'rear') {
					this.cameraFacing = 'front'
				}
			}
		},
		// footer: 내 스탬프 (메인으로)
		goMain() {
			this.activeTab = 'main'
			this.noticeDialog = false
			this.scanDialog = false
			this.pinDialog = false
			window.scrollTo({ top: 0, behavior: 'smooth' })
		},
		// footer: 안내 (공지사항)
		async openNotice() {
			this.activeTab = 'notice'
			this.noticeDialog = true
			await this.fetchNotices()
		},
		closeNotice() {
			this.noticeDialog = false
			this.activeTab = 'main'
		},
		// 공지사항 목록 조회 (등록은 관리자에서 진행)
		async fetchNotices() {
			this.noticeLoading = true
			try {
				const res = await axios.get(BACKEND + '/stamp-notices', {
					params: { groupcode: this.groupcode },
				})
				this.notices = res.data.notices || []
			} catch (err) {
				console.log({ err })
				this.showToast('공지사항을 불러오지 못했습니다.', 'error')
			} finally {
				this.noticeLoading = false
			}
		},
		formatNoticeDate(v) {
			if (!v) return ''
			const d = new Date(v)
			if (isNaN(d.getTime())) return String(v)
			const p = n => String(n).padStart(2, '0')
			return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`
		},
		async onDecode(content) {
			if (!content || this.scanBusy) return
			this.scanBusy = true
			try {
				// QR 내용이 URL 형태여도 마지막 경로 조각을 그룹 코드로 사용
				const qrCode = String(content)
					.trim()
					.split('?')[0]
					.split('/')
					.filter(x => x)
					.pop()
				const res = await axios.post(BACKEND + '/stamp', {
					groupcode: this.groupcode,
					phone: this.user.phone,
					name: this.user.name,
					qrCode,
				})
				if (res.data.result === 'ok') {
					this.scanDialog = false
					this.showToast(`[${res.data.group}] 스탬프가 적립되었습니다!`)
					await this.fetchTours()
					// 도장 찍히는 연출: 방금 적립된 스탬프 원에 애니메이션
					this.justStamped = { tourCode: res.data.tourCode || '', groupTitle: res.data.group || '' }
					setTimeout(() => {
						this.justStamped = { tourCode: '', groupTitle: '' }
					}, 900)
				} else if (res.data.result === 'already') {
					this.scanDialog = false
					this.showToast('이미 적립된 스탬프입니다.', 'warning')
				} else if (res.data.result === 'closed') {
					this.showToast('지금은 운영 기간이 아닙니다.', 'error')
				} else {
					this.showToast('유효하지 않은 QR코드입니다.', 'error')
				}
			} catch (err) {
				console.log({ err })
				this.showToast('스탬프 적립 중 오류가 발생하였습니다.', 'error')
			} finally {
				setTimeout(() => {
					this.scanBusy = false
					this.scanKey++
				}, 1200)
			}
		},
		// 룰렛 세그먼트 구성 (경품 + 꽝)
		buildSegments(tour) {
			let priceList = tour.event.priceList || []
			if (typeof priceList === 'string') {
				try {
					priceList = JSON.parse(priceList)
				} catch (e) {
					priceList = []
				}
			}
			const segs = priceList.filter(p => p.useYn !== false).map(p => p.product)
			const fail = tour.event.failPrice || '꽝'
			if (segs.length === 0) return [fail]
			// 짝수 칸 유지를 위해 꽝 1칸 추가
			return [...segs, fail]
		},
		segmentLabelStyle(index) {
			const n = this.wheelSegments.length || 1
			const arc = 360 / n
			return { transform: `rotate(${index * arc + arc / 2}deg) translateY(-92px)` }
		},
		// 추첨 진행 (기존 createEventJoin 모듈 재사용 - 당첨 여부는 서버가 결정)
		async startDraw(tour) {
			if (this.drawing) return
			this.drawing = true
			try {
				const res = await this.$store.dispatch('createEventJoin', {
					name: this.user.name,
					phone: this.user.phone,
					code: tour.event.code,
					marketing: true,
				})
				const join = res.data.createEventJoin.eventJoin
				if (join.name === '중복번호') {
					this.showToast('오늘은 이미 참여하셨습니다.', 'warning')
					await this.fetchTours()
					return
				}
				if (join.name === '이벤트 종료') {
					this.showToast('이벤트가 종료되었습니다.', 'warning')
					return
				}
				// 서버 검증: 당일 스탬프를 모두 모으지 않은 상태에서는 추첨 불가
				if (join.name === '스탬프 미완주') {
					this.showToast('스탬프를 모두 모은 뒤 참여할 수 있습니다.', 'warning')
					await this.fetchTours()
					return
				}
				this.drawProduct = join.winnterProduct || ''
				if (tour.event.type === 'roulette') {
					this.spinRoulette(tour)
				} else {
					await this.fetchTours()
					if (this.drawProduct) this.showToast(`${this.drawProduct} 당첨! 경품 수령을 진행해주세요.`)
				}
			} catch (err) {
				console.log({ err })
				this.showToast('추첨 진행 중 오류가 발생하였습니다.', 'error')
			} finally {
				this.drawing = false
			}
		},
		// 룰렛 회전 연출 (결과는 이미 서버에서 확정)
		spinRoulette(tour) {
			this.rouletteTour = tour
			this.spinDone = false
			this.wheelRotate = 0
			this.rouletteDialog = true
			const segments = this.buildSegments(tour)
			const n = segments.length
			const arc = 360 / n
			let target = segments.indexOf(this.drawProduct)
			if (target < 0) target = n - 1 // 미당첨 → 꽝 칸
			setTimeout(() => {
				// 핀(12시 방향)에 당첨 칸 중앙이 오도록 회전
				this.wheelRotate = 360 * 8 - (target * arc + arc / 2)
			}, 300)
			setTimeout(async () => {
				this.spinDone = true
				await this.fetchTours()
			}, 4600)
		},
		closeRoulette() {
			this.rouletteDialog = false
			this.rouletteTour = null
			this.wheelRotate = 0
		},
		// 핀코드 수령 처리
		openPin(tour) {
			this.pinTour = tour
			this.pinDigits = Array(PIN_LENGTH).fill('')
			this.pinError = ''
			this.pinDialog = true
			this.$nextTick(() => this.focusPin(0))
		},
		focusPin(i) {
			const el = this.$refs['pin' + i]
			if (el && el[0]) el[0].focus()
		},
		// 한 칸에 숫자 1자리 입력 → 다음 칸으로 자동 이동
		onPinInput(i, e) {
			const v = (e.target.value || '').replace(/[^0-9]/g, '').slice(-1)
			e.target.value = v
			this.$set(this.pinDigits, i, v)
			this.pinError = ''
			if (v && i < PIN_LENGTH - 1) this.focusPin(i + 1)
		},
		onPinKeydown(i, e) {
			if (e.key === 'Backspace' && !this.pinDigits[i] && i > 0) this.focusPin(i - 1)
			if (e.key === 'Enter' && this.pinReady) this.submitPin()
		},
		onPinPaste(e) {
			const digits = ((e.clipboardData && e.clipboardData.getData('text')) || '').replace(/[^0-9]/g, '').slice(0, PIN_LENGTH)
			digits.split('').forEach((d, i) => this.$set(this.pinDigits, i, d))
			if (digits.length) this.focusPin(Math.min(digits.length, PIN_LENGTH) - 1)
		},
		async submitPin() {
			if (!this.pinReady) {
				this.pinError = '확인 코드를 입력해주세요.'
				return
			}
			this.pinLoading = true
			this.pinError = ''
			try {
				const res = await axios.post(BACKEND + '/event-joins/stamp-receipt', {
					reservedayCode: this.pinTour.code,
					phone: this.user.phone,
					name: this.user.name,
					staffCode: this.pinCode,
				})
				if (res.data.result === 'ok' || res.data.result === 'already') {
					this.pinDialog = false
					await this.fetchTours()
					// 만족도 조사는 참여자당 1회만 노출 (이미 참여했으면 스킵)
					if (res.data.surveyLink && !this.isSurveyDone()) {
						this.surveyLink = res.data.surveyLink
						this.surveyTourTitle = this.pinTour ? this.pinTour.title : ''
						this.surveyDialog = true
						this.markSurveyDone()
					}
				} else if (res.data.result === 'wrong_code') {
					this.pinError = '확인 코드가 올바르지 않습니다.'
					this.pinDigits = Array(PIN_LENGTH).fill('')
					this.$nextTick(() => this.focusPin(0))
				} else if (res.data.result === 'no_join') {
					this.pinError = '오늘 추첨 내역이 없습니다.'
				} else if (res.data.result === 'not_complete') {
					this.pinError = '오늘 스탬프를 모두 모은 내역이 없습니다.'
				} else {
					this.pinError = '수령 처리 중 오류가 발생하였습니다.'
				}
			} catch (err) {
				console.log({ err })
				this.pinError = '수령 처리 중 오류가 발생하였습니다.'
			} finally {
				this.pinLoading = false
			}
		},
		openSurvey() {
			if (this.surveyLink) window.open(this.surveyLink, '_blank')
			this.surveyDialog = false
		},
		// 만족도 조사 노출 여부 (참여자당 1회) — 다른 존에서 이미 참여했으면 재노출 안 함
		surveyKey() {
			return 'stamp-survey-done-' + this.groupcode + '-' + (this.user ? this.user.phone : '')
		},
		isSurveyDone() {
			return this.surveyDone
		},
		markSurveyDone() {
			sessionStorage.setItem(this.surveyKey(), '1')
			this.surveyDone = true
		},
		// 미당첨(꽝) 등 경품 수령 절차가 없는 경우에도 만족도 조사에 참여할 수 있도록 팝업 노출
		openSurveyForTour(tour) {
			if (!tour.endLink || this.surveyDone) return
			this.surveyLink = tour.endLink
			this.surveyTourTitle = tour.title
			this.surveyDialog = true
			this.markSurveyDone()
		},
	},
}
</script>

<style>
@import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css');
</style>

<style lang="scss" scoped>
$blue: #118fd8;
$blue-strong: #087dc5;
$yellow: #ffd733;
$ink: #1c252e;
$line: #e6ebf0;
$danger: #ed4036;
$shadow: 0 9px 24px rgba(30, 65, 93, 0.09);
$font: 'Pretendard Variable', 'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', system-ui, -apple-system, sans-serif;

.stamp-main-page {
	min-height: 100vh;
	display: flex;
	justify-content: center;
	align-items: flex-start; // 앱 높이가 컨텐츠만큼 늘어나도록 (stretch 시 하단 잘림)
	background: #eef3f7;
	font-family: $font;
	color: $ink;
	* {
		box-sizing: border-box;
	}
	button,
	a {
		font-family: inherit;
	}
	button {
		cursor: pointer;
	}
}
.stamp-app {
	width: 100%;
	max-width: 430px;
	min-height: 100vh;
	background: #f8fafc;
	position: relative;
	padding-bottom: 82px;
	overflow-x: hidden;
}
// 상단 바
.stamp-topbar {
	height: 62px;
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 14px;
	border-bottom: 1px solid $line;
	position: sticky;
	top: 0;
	z-index: 20;
	gap: 8px;
}
.stamp-brand {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	flex: 1;
	img {
		width: 88px;
		height: auto;
		display: block;
		flex: 0 0 auto;
	}
}
.stamp-brand-sep {
	width: 1px;
	height: 22px;
	background: #dde3e9;
	flex: 0 0 auto;
}
.stamp-brand-title {
	font-size: 14px;
	font-weight: 900;
	letter-spacing: -0.035em;
	white-space: nowrap;
}
.stamp-week-btn {
	height: 36px;
	padding: 0 11px;
	border: 1px solid #dde5ec;
	border-radius: 999px;
	background: #fff;
	color: #5c6975;
	font-size: 11px;
	font-weight: 800;
	flex: 0 0 auto;
}
.stamp-logout {
	width: 34px;
	height: 34px;
	flex: 0 0 auto;
	display: grid;
	place-items: center;
	border-radius: 50%;
	background: transparent;
	.v-icon {
		color: #9aa4ad;
	}
	&:active {
		background: #eef2f5;
	}
}
.stamp-hero {
	background: $yellow;
	height: 196px;
	overflow: hidden;
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
}
.stamp-body {
	padding: 12px 14px 26px;
}
// 경품 수령(팔찌) 안내
.stamp-notice {
	position: sticky;
	top: 70px;
	z-index: 12;
	display: flex;
	gap: 10px;
	align-items: flex-start;
	background: #fff;
	border: 1px solid #ffd1ce;
	border-left: 5px solid $danger;
	border-radius: 14px;
	padding: 12px 12px 12px 10px;
	box-shadow: 0 6px 16px rgba(31, 57, 79, 0.06);
	margin-bottom: 16px;
	strong {
		display: block;
		font-size: 14px;
		font-weight: 900;
		margin-bottom: 3px;
	}
	p {
		margin: 0;
		color: #626d78;
		font-size: 11.7px;
		line-height: 1.48;
		letter-spacing: -0.02em;
	}
	b {
		color: $danger;
	}
}
.stamp-notice-icon {
	width: 26px;
	height: 26px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	flex: 0 0 auto;
	background: $danger;
	color: #fff;
	font-weight: 900;
	font-size: 17px;
	line-height: 1;
}
.stamp-section-title {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	margin: 18px 2px 10px;
	h1 {
		margin: 0;
		font-size: 21px;
		font-weight: 900;
		letter-spacing: -0.045em;
	}
	span {
		font-size: 11.5px;
		color: #8b949d;
	}
}
.stamp-main-loading {
	text-align: center;
	padding: 60px 0;
}
.stamp-main-empty {
	text-align: center;
	color: #8b949d;
	font-size: 14px;
	padding: 40px 0;
}
// 투어 카드
.stamp-tour-card {
	background: #fff;
	border-radius: 18px;
	padding: 15px;
	box-shadow: $shadow;
	margin-bottom: 12px;
	border: 1px solid #edf0f3;
	position: relative;
	overflow: hidden;
	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: 0;
		bottom: 0;
		width: 5px;
		background: $yellow;
	}
	&.is-done::before {
		background: $blue;
	}
}
.stamp-card-head {
	display: flex;
	align-items: center;
	gap: 10px;
}
.stamp-thumb {
	width: 74px;
	height: 56px;
	border-radius: 14px;
	overflow: hidden;
	flex: 0 0 auto;
	border: 1px solid #f0e7af;
	background: #fff8d9;
	display: grid;
	place-items: center;
	cursor: pointer;
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
}
.stamp-meta {
	min-width: 0;
	flex: 1;
	h2 {
		margin: 0 0 4px;
		font-size: 16.5px;
		font-weight: 900;
		letter-spacing: -0.035em;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}
.stamp-progress {
	display: flex;
	align-items: center;
	gap: 6px;
	font-weight: 900;
	font-size: 13.5px;
	color: $blue-strong;
	flex-wrap: wrap;
	small {
		font-size: 10.5px;
		font-weight: 700;
		color: #87929d;
		white-space: pre-wrap;
	}
}
.stamp-state {
	border-radius: 999px;
	background: $yellow;
	padding: 9px 12px;
	white-space: nowrap;
	font-size: 12px;
	font-weight: 900;
	color: #4a3b00;
	flex: 0 0 auto;
	&.done {
		background: #eaf7ff;
		color: $blue-strong;
	}
}
// 스탬프 원 + 명칭 라벨
.stamp-row {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 13px;
	padding-left: 3px;
	min-height: 55px;
	flex-wrap: wrap;
	&.labeled {
		align-items: flex-start;
		gap: 6px;
		min-height: 70px;
	}
}
.stamp-item {
	width: 46px;
	flex: 0 0 46px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-start;
	gap: 6px;
}
.stamp-row:not(.labeled) .stamp-item {
	gap: 0;
}
.stamp-slot {
	position: relative;
	width: 46px;
	height: 46px;
	border: 2px dashed #d7dde3;
	border-radius: 50%;
	display: grid;
	place-items: center;
	flex: 0 0 auto;
	background: #fff;
	img {
		width: 44px;
		height: 44px;
		object-fit: contain;
	}
	&.filled {
		border-color: transparent;
	}
	// 도장 찍히는 연출: 크게 회전하며 내려와 바운스로 안착
	&.just-stamped {
		animation: stampDrop 0.55s cubic-bezier(0.2, 0.8, 0.3, 1.4) both;
		// 잉크 링(충격파) 효과
		&::after {
			content: '';
			position: absolute;
			inset: -4px;
			border-radius: 50%;
			border: 3px solid $yellow;
			animation: stampRipple 0.6s ease-out both;
			pointer-events: none;
		}
	}
}
.stamp-name {
	font-family: inherit;
	font-size: 10px;
	line-height: 1.2;
	font-weight: 800;
	letter-spacing: -0.035em;
	color: #697684;
	white-space: nowrap;
	text-align: center;
}
@keyframes stampDrop {
	0% {
		transform: scale(2.4) rotate(-20deg);
		opacity: 0;
	}
	55% {
		transform: scale(0.88) rotate(7deg);
		opacity: 1;
	}
	78% {
		transform: scale(1.08) rotate(-3deg);
	}
	100% {
		transform: scale(1) rotate(0deg);
	}
}
@keyframes stampRipple {
	0% {
		transform: scale(0.6);
		opacity: 0.9;
	}
	100% {
		transform: scale(1.7);
		opacity: 0;
	}
}
.stamp-completion-note {
	margin-top: 10px;
	display: flex;
	align-items: flex-start;
	gap: 6px;
	color: #648096;
	font-size: 11px;
	font-weight: 700;
	line-height: 1.45;
}
.stamp-check-dot {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	background: #dff3ff;
	color: $blue-strong;
	font-size: 10px;
	font-weight: 900;
	flex: 0 0 auto;
	margin-top: 1px;
}
// 완주/경품 수령 영역
.stamp-reward-ready {
	margin-top: 14px;
	padding-top: 14px;
	border-top: 1px solid #edf1f4;
}
.stamp-reward-copy {
	display: flex;
	align-items: flex-start;
	gap: 7px;
	margin-bottom: 11px;
	color: #687987;
	font-size: 11.5px;
	font-weight: 750;
	line-height: 1.5;
	b {
		color: #e6832a;
	}
}
.stamp-gift-dot {
	width: 18px;
	height: 18px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	flex: 0 0 auto;
	background: #fff2b8;
	color: #7a6200;
	font-size: 11px;
	font-weight: 1000;
	margin-top: 1px;
}
.stamp-reward-btn {
	width: 100%;
	height: 52px;
	border: 0;
	border-radius: 14px;
	background: linear-gradient(180deg, #169dea 0%, #0b84ce 100%);
	color: #fff;
	font-size: 16px;
	font-weight: 900;
	letter-spacing: -0.025em;
	box-shadow: 0 8px 16px rgba(8, 125, 197, 0.2);
	&:active {
		transform: translateY(1px);
	}
	&:disabled {
		opacity: 0.7;
	}
}
.stamp-reward-finished {
	margin-top: 13px;
	padding: 13px 14px;
	border-radius: 14px;
	background: #f1faf5;
	border: 1px solid #cdeedc;
	color: #28764a;
	font-size: 12px;
	font-weight: 800;
	line-height: 1.5;
}
.stamp-sub-btn {
	width: 100%;
	height: 46px;
	margin-top: 8px;
	border: 1px solid #d5dde5;
	border-radius: 14px;
	background: #fff;
	color: #596673;
	font-size: 14px;
	font-weight: 850;
}
.stamp-helper {
	margin: 12px 2px 0;
	color: #7d8791;
	font-size: 11.5px;
	line-height: 1.55;
}
// 하단 footer 네비게이션
.stamp-bottom-nav {
	position: fixed;
	left: 50%;
	bottom: 0;
	transform: translateX(-50%);
	width: 100%;
	max-width: 430px;
	height: 74px;
	background: #fff;
	border-top: 1px solid $line;
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	z-index: 25;
	padding-bottom: max(4px, env(safe-area-inset-bottom));
}
.stamp-nav-item {
	border: 0;
	background: transparent;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	color: #8a949e;
	font-size: 10.5px;
	font-weight: 800;
	&.active {
		color: $blue-strong;
	}
}
.stamp-nav-icon {
	width: 23px;
	height: 23px;
	display: block;
	svg {
		width: 100%;
		height: 100%;
		display: block;
		stroke: currentColor;
		fill: none;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
	}
}
// 경품 수령 확인 화면
.stamp-claim-screen {
	position: fixed;
	left: 50%;
	top: 0;
	bottom: 0;
	width: 100%;
	max-width: 430px;
	transform: translateX(-50%);
	z-index: 70;
	background: #f8fafc;
	overflow: auto;
	box-shadow: 0 0 35px rgba(20, 40, 58, 0.1);
}
.stamp-claim-topbar {
	height: 62px;
	background: #fff;
	border-bottom: 1px solid $line;
	display: flex;
	align-items: center;
	padding: 0 16px;
	position: sticky;
	top: 0;
	z-index: 2;
	strong {
		font-size: 17px;
		font-weight: 900;
		letter-spacing: -0.035em;
	}
}
.stamp-claim-back {
	width: 38px;
	height: 38px;
	border: 0;
	border-radius: 50%;
	background: #f3f6f8;
	color: #50606e;
	font-size: 22px;
	display: grid;
	place-items: center;
	margin-right: 9px;
}
.stamp-claim-body {
	padding: 24px 18px 34px;
}
.stamp-claim-logo {
	display: flex;
	justify-content: center;
	margin: 5px 0 18px;
	img {
		width: 154px;
		height: auto;
		display: block;
	}
}
.stamp-claim-card {
	background: #fff;
	border: 1px solid #e7ecf1;
	border-radius: 22px;
	padding: 24px 20px 22px;
	box-shadow: $shadow;
	h1 {
		margin: 0;
		text-align: center;
		font-size: 23px;
		font-weight: 900;
		letter-spacing: -0.045em;
	}
	.lead {
		margin: 9px auto 22px;
		max-width: 300px;
		color: #71808d;
		text-align: center;
		font-size: 13px;
		line-height: 1.55;
		letter-spacing: -0.025em;
	}
}
.stamp-claim-icon {
	width: 54px;
	height: 54px;
	margin: 0 auto 14px;
	border-radius: 18px;
	background: #eaf6fe;
	color: $blue-strong;
	display: grid;
	place-items: center;
	font-size: 25px;
	font-weight: 1000;
}
.stamp-pin-label {
	display: block;
	font-size: 13px;
	font-weight: 900;
	margin-bottom: 9px;
}
.stamp-pin-row {
	display: grid;
	gap: 9px;
	width: 100%;
	min-width: 0;
}
.stamp-pin-box {
	display: block;
	width: 100%;
	min-width: 0;
	height: 60px;
	padding: 0;
	border: 1.5px solid #d4dde5;
	border-radius: 13px;
	background: #fff;
	text-align: center;
	font-size: 25px;
	font-weight: 900;
	outline: none;
	color: $ink;
	font-family: inherit;
	appearance: none;
	-webkit-appearance: none;
	&:focus {
		border-color: $blue;
		box-shadow: 0 0 0 4px rgba(17, 143, 216, 0.1);
	}
}
.stamp-staff-note {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin: 15px 0 20px;
	padding: 12px 13px;
	border-radius: 13px;
	background: #f4f8fc;
	color: #647687;
	font-size: 11.5px;
	line-height: 1.5;
}
.stamp-info-dot {
	width: 18px;
	height: 18px;
	border-radius: 50%;
	border: 1.5px solid #7b9ab5;
	display: grid;
	place-items: center;
	flex: 0 0 auto;
	color: #577b99;
	font-size: 11px;
	font-weight: 1000;
	margin-top: 1px;
}
.stamp-pin-error {
	margin: -8px 0 12px;
	color: $danger;
	font-size: 13px;
	font-weight: 700;
	text-align: center;
}
.stamp-confirm-btn {
	width: 100%;
	height: 54px;
	border: 0;
	border-radius: 14px;
	background: #b8c6d2;
	color: #fff;
	font-size: 16px;
	font-weight: 900;
	transition: 0.15s;
	&.ready {
		background: linear-gradient(180deg, #169dea, #0b84ce);
		box-shadow: 0 8px 16px rgba(8, 125, 197, 0.18);
	}
}
.stamp-claim-security {
	margin: 17px auto 0;
	max-width: 300px;
	text-align: center;
	color: #97a3ad;
	font-size: 10.5px;
	line-height: 1.5;
}
// 동물행복위크 팝업
.stamp-week-modal {
	background: #fff;
	border-radius: 26px;
	overflow: hidden;
	font-family: $font;
	color: $ink;
}
.stamp-week-head {
	position: relative;
	padding: 24px 22px 16px;
	text-align: center;
	background: linear-gradient(180deg, #fff9db 0%, #ffffff 100%);
	h2 {
		margin: 0 0 7px;
		font-size: 22px;
		font-weight: 900;
		letter-spacing: -0.045em;
	}
	p {
		margin: 0;
		color: #6f7882;
		font-size: 13px;
		line-height: 1.5;
	}
}
.stamp-week-logo {
	width: 142px;
	height: auto;
	display: block;
	margin: 0 auto 14px;
}
.stamp-week-kicker {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: #eaf6fe;
	color: $blue-strong;
	border-radius: 999px;
	padding: 7px 10px;
	font-size: 11px;
	font-weight: 900;
	margin-bottom: 10px;
}
.stamp-week-close {
	position: absolute;
	right: 14px;
	top: 14px;
	width: 36px;
	height: 36px;
	border: 0;
	border-radius: 50%;
	background: rgba(255, 255, 255, 0.82);
	color: #69737d;
	font-size: 24px;
	line-height: 1;
	cursor: pointer;
}
.stamp-event-list {
	padding: 4px 16px 10px;
}
.stamp-event-card {
	display: grid;
	grid-template-columns: 44px 1fr 26px;
	align-items: center;
	gap: 11px;
	width: 100%;
	min-height: 76px;
	margin: 9px 0;
	padding: 12px;
	border: 1px solid #e6ebf0;
	border-radius: 16px;
	background: #fff;
	text-decoration: none;
	color: $ink;
	box-shadow: 0 4px 13px rgba(30, 65, 93, 0.045);
	&.current {
		border-color: #b9e1f8;
		background: #f5fbff;
	}
	&.no1 .stamp-event-no {
		background: #eaf6fe;
		color: $blue-strong;
	}
	&.no2 .stamp-event-no {
		background: #f2eef9;
		color: #66528b;
	}
}
.stamp-event-no {
	width: 44px;
	height: 44px;
	border-radius: 14px;
	display: grid;
	place-items: center;
	background: $yellow;
	font-weight: 900;
	color: #514200;
	font-size: 13px;
}
.stamp-event-info {
	min-width: 0;
	strong {
		display: block;
		font-size: 15px;
		font-weight: 800;
		letter-spacing: -0.035em;
		margin-bottom: 4px;
	}
	span {
		display: block;
		color: #84909a;
		font-size: 11.5px;
		line-height: 1.35;
	}
}
.stamp-event-arrow {
	color: #a4afb9;
	font-size: 22px;
	text-align: center;
}
.stamp-week-benefit {
	margin: 4px 16px 16px;
	padding: 13px 14px;
	border-radius: 14px;
	background: #fff8d9;
	color: #675a1b;
	font-size: 11.8px;
	line-height: 1.5;
	strong {
		font-weight: 900;
		color: #473b00;
	}
}
.stamp-week-bottom {
	padding: 0 16px 18px;
	button {
		width: 100%;
		height: 50px;
		border: 1px solid #ccd4dc;
		border-radius: 13px;
		background: #fff;
		color: #515b65;
		font-size: 14px;
		font-weight: 900;
		cursor: pointer;
	}
}
// 만족도 조사 바텀시트
.stamp-survey-sheet {
	background: #fff;
	border-radius: 26px 26px 0 0;
	padding: 12px 18px max(22px, env(safe-area-inset-bottom));
	font-family: $font;
	color: $ink;
	h2 {
		margin: 0;
		text-align: center;
		font-size: 22px;
		font-weight: 900;
		letter-spacing: -0.045em;
	}
	p {
		margin: 9px auto 18px;
		max-width: 330px;
		text-align: center;
		color: #6f7c88;
		font-size: 13px;
		line-height: 1.55;
		letter-spacing: -0.02em;
	}
}
.stamp-sheet-handle {
	width: 44px;
	height: 5px;
	border-radius: 999px;
	background: #d8dee4;
	margin: 2px auto 18px;
}
.stamp-survey-icon {
	width: 58px;
	height: 58px;
	border-radius: 19px;
	background: #fff8d9;
	display: grid;
	place-items: center;
	margin: 0 auto 13px;
	font-size: 27px;
}
.stamp-survey-primary {
	width: 100%;
	height: 52px;
	border: 0;
	border-radius: 14px;
	background: linear-gradient(180deg, #169dea, #0b84ce);
	color: #fff;
	font-size: 15px;
	font-weight: 900;
	cursor: pointer;
}
.stamp-survey-close {
	width: 100%;
	height: 48px;
	margin-top: 8px;
	border: 1px solid #d5dde5;
	border-radius: 14px;
	background: #fff;
	color: #596673;
	font-size: 14px;
	font-weight: 850;
	cursor: pointer;
}
// 안내(공지사항)
.stamp-notice-page {
	background: #f8fafc;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	font-family: $font;
	color: $ink;
}
.stamp-notice-head {
	display: flex;
	justify-content: space-between;
	align-items: center;
	background: #fff;
	font-size: 17px;
	font-weight: 800;
	letter-spacing: -0.035em;
	padding: 12px 8px 12px 20px;
	border-bottom: 1px solid $line;
}
.stamp-notice-body {
	flex: 1;
	width: 100%;
	max-width: 430px;
	margin: 0 auto;
	padding: 16px 14px;
}
.stamp-notice-loading,
.stamp-notice-empty {
	text-align: center;
	color: #8b949d;
	font-size: 14px;
	padding: 60px 0;
}
.stamp-notice-card {
	background: #fff;
	border: 1px solid #edf0f3;
	border-radius: 18px;
	box-shadow: $shadow;
	padding: 16px;
	margin-bottom: 12px;
}
.stamp-notice-title {
	font-size: 16px;
	font-weight: 800;
	letter-spacing: -0.035em;
	margin-bottom: 4px;
}
.stamp-notice-date {
	font-size: 12px;
	color: #8b949d;
	margin-bottom: 10px;
}
.stamp-notice-content {
	font-size: 14px;
	color: #4f5b66;
	line-height: 22px;
	white-space: pre-wrap;
}
// 공지 첨부 이미지
.stamp-notice-images {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 12px;
}
.stamp-notice-img {
	width: 100%;
	max-width: 100%;
	border-radius: 10px;
	border: 1px solid $line;
	cursor: pointer;
	object-fit: cover;
}
// 공지 첨부 파일 (다운로드)
.stamp-notice-files {
	margin-top: 12px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}
.stamp-notice-file {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 9px 11px;
	background: #f4f8fc;
	border: 1px solid #dceaf5;
	border-radius: 10px;
	text-decoration: none;
	color: $blue-strong;
	font-size: 13px;
}
.stamp-notice-file-name {
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.stamp-notice-file-size {
	font-size: 11px;
	color: #97a3ad;
}
// 이미지 뷰어
.stamp-image-viewer {
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 60vh;
}
.stamp-image-viewer-img {
	max-width: 100%;
	max-height: 90vh;
	object-fit: contain;
}
// QR 스캔
.stamp-scan-page {
	background: #000;
	height: 100vh; // 플렉스 자식(카메라)이 100% 높이를 해석하려면 확정 높이 필요
	display: flex;
	flex-direction: column;
	overflow: hidden;
	font-family: $font;
}
.stamp-scan-head {
	display: flex;
	justify-content: space-between;
	align-items: center;
	color: #fff;
	font-size: 17px;
	font-weight: 700;
	padding: 14px 8px 14px 20px;
}
.stamp-scan-reader {
	flex: 1 1 auto;
	min-height: 0;
	height: 100%;
	overflow: hidden;
	// 카메라 영상이 남는 영역을 꽉 채우도록
	::v-deep video,
	::v-deep .qrcode-stream-camera {
		width: 100% !important;
		height: 100% !important;
		object-fit: cover !important;
	}
	// 오버레이(가이드 프레임)를 전체 높이로 채우고 중앙 정렬
	::v-deep .qrcode-stream-overlay {
		display: flex;
		align-items: center;
		justify-content: center;
	}
}
// QR 정렬 가이드 프레임
.stamp-scan-frame {
	position: relative;
	width: 240px;
	height: 240px;
	max-width: 72vw;
	max-height: 72vw;
	pointer-events: none;
}
.stamp-scan-corner {
	position: absolute;
	width: 34px;
	height: 34px;
	border: 4px solid #fff;
	box-shadow: 0 0 4px rgba(0, 0, 0, 0.4);
	&.tl {
		top: 0;
		left: 0;
		border-right: 0;
		border-bottom: 0;
		border-top-left-radius: 8px;
	}
	&.tr {
		top: 0;
		right: 0;
		border-left: 0;
		border-bottom: 0;
		border-top-right-radius: 8px;
	}
	&.bl {
		bottom: 0;
		left: 0;
		border-right: 0;
		border-top: 0;
		border-bottom-left-radius: 8px;
	}
	&.br {
		bottom: 0;
		right: 0;
		border-left: 0;
		border-top: 0;
		border-bottom-right-radius: 8px;
	}
}
.stamp-scan-footer {
	margin-top: auto;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	background: #000;
	padding: 14px 20px calc(14px + env(safe-area-inset-bottom));
}
.stamp-scan-footer-guide {
	flex: 1;
	color: #fff;
	font-size: 14px;
}
.stamp-scan-footer-btn {
	flex-shrink: 0;
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	background: rgba(255, 255, 255, 0.15);
	border: 1px solid rgba(255, 255, 255, 0.45);
	border-radius: 20px;
	color: #fff;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	transition: background-color 0.2s;
	&:active {
		background: rgba(255, 255, 255, 0.3);
	}
}
// 룰렛
.stamp-roulette-box {
	background: #fff;
	border-radius: 22px;
	padding: 24px 20px;
	text-align: center;
	font-family: $font;
	color: $ink;
}
.stamp-roulette-title {
	font-size: 20px;
	font-weight: 900;
	letter-spacing: -0.045em;
	margin-bottom: 18px;
}
.stamp-roulette-wrap {
	position: relative;
	width: 240px;
	height: 240px;
	margin: 0 auto 18px;
}
.stamp-roulette-pin {
	position: absolute;
	top: -14px;
	left: 50%;
	transform: translateX(-50%);
	font-size: 22px;
	color: $danger;
	z-index: 2;
}
.stamp-roulette-wheel {
	width: 240px;
	height: 240px;
	border-radius: 50%;
	border: 6px solid $blue;
	position: relative;
	overflow: hidden;
}
.stamp-roulette-label {
	position: absolute;
	top: 50%;
	left: 50%;
	width: 84px;
	margin-left: -42px;
	margin-top: -8px;
	font-size: 11px;
	font-weight: 800;
	color: #333;
	transform-origin: center;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.stamp-roulette-result {
	font-size: 17px;
	font-weight: 900;
	color: #e6832a;
	margin-bottom: 12px;
}
@media (max-width: 360px) {
	.stamp-brand img {
		width: 80px;
	}
	.stamp-hero {
		height: 180px;
	}
	.stamp-body {
		padding-left: 11px;
		padding-right: 11px;
	}
	.stamp-slot {
		width: 42px;
		height: 42px;
		img {
			width: 40px;
			height: 40px;
		}
	}
	.stamp-item {
		width: 42px;
		flex-basis: 42px;
	}
	.stamp-name {
		font-size: 9.5px;
	}
	.stamp-row.labeled {
		gap: 5px;
		min-height: 66px;
	}
	.stamp-thumb {
		width: 68px;
		height: 54px;
	}
	.stamp-claim-body {
		padding-left: 12px;
		padding-right: 12px;
	}
	.stamp-claim-card {
		padding-left: 15px;
		padding-right: 15px;
	}
	.stamp-pin-row {
		gap: 7px;
	}
	.stamp-pin-box {
		height: 56px;
		font-size: 23px;
	}
}
@media (min-width: 431px) {
	// 넓은 화면: 위쪽만 띄운 카드 형태, 하단은 화면 끝까지 붙여 하단 메뉴가 떠 보이지 않게 한다
	.stamp-app {
		margin: 18px 0 0;
		border-radius: 26px 26px 0 0;
		min-height: calc(100vh - 18px);
		box-shadow: 0 20px 60px rgba(18, 40, 60, 0.14);
		overflow: hidden;
	}
	.stamp-topbar {
		position: relative;
	}
	.stamp-bottom-nav {
		bottom: 0;
		box-shadow: 0 -6px 18px rgba(18, 40, 60, 0.06);
	}
}
</style>

<style lang="scss">
// v-dialog 컨텐츠 래퍼 (scoped 밖) - 동물행복위크 팝업 라운드
.stamp-week-dialog {
	border-radius: 26px !important;
}
</style>
